// Thin client for the same-origin /api. Never hard-codes the domain.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...rest,
    headers: { accept: 'application/json', ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.status === 204) return undefined as T;
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, b.error ?? 'error', b.message ?? `Request failed (${res.status})`, body);
  }
  return body as T;
}

export interface ServerConfig {
  env: string;
  features: { accounts: boolean; billing: boolean; microsoft?: boolean };
  licensePrice: { amount: number; currency: string };
}

export interface User {
  id: string;
  email: string;
  license: 'none' | 'lifetime';
}

export interface FeedItem {
  title: string;
  link: string | null;
  published: string | null;
}

export interface CalendarEvent {
  uid: string;
  title: string;
  location: string | null;
  start: string;
  end: string;
  allDay: boolean;
}

export interface DriveFolder {
  id: string;
  name: string;
  childCount: number;
}

export interface DriveListing {
  folder: { id: string | null; name: string; parentId: string | null };
  folders: DriveFolder[];
  mediaCount: number;
}

export interface DriveMedia {
  id: string;
  name: string;
  kind: 'image' | 'video';
  takenAt: string | null;
}

export const endpoints = {
  config: () => api<ServerConfig>('/config'),
  me: () => api<{ user: User | null }>('/auth/me'),
  login: (email: string, password: string) => api<{ user: User }>('/auth/login', { method: 'POST', json: { email, password } }),
  register: (email: string, password: string) =>
    api<{ user: User }>('/auth/register', { method: 'POST', json: { email, password } }),
  logout: () => api<void>('/auth/logout', { method: 'POST' }),
  getSettings: () => api<{ data: Record<string, unknown> | null; version: string | null }>('/settings'),
  putSettings: (data: Record<string, unknown>, baseVersion: string | null) =>
    api<{ version: string }>('/settings', { method: 'PUT', json: { data, baseVersion } }),
  checkout: () => api<{ url: string }>('/billing/checkout', { method: 'POST' }),
  microsoftStatus: () => api<{ connected: boolean; email: string | null }>('/microsoft/status'),
  microsoftDisconnect: () => api<void>('/microsoft', { method: 'DELETE' }),
  driveChildren: (id?: string) =>
    api<DriveListing>(`/microsoft/drive/children${id ? `?id=${encodeURIComponent(id)}` : ''}`),
  driveMedia: (folderId: string) =>
    api<{ items: DriveMedia[] }>(`/microsoft/drive/media?folderId=${encodeURIComponent(folderId)}`),
  outlookCalendar: (days: number) => api<{ events: CalendarEvent[] }>(`/microsoft/calendar?days=${days}`),
  pairStart: () =>
    api<{ pairingId: string; pollToken: string; code: string; expiresAt: string }>('/pair/start', { method: 'POST' }),
  pairPoll: (pairingId: string, pollToken: string) =>
    api<{ status: 'pending' | 'approved' | 'expired' }>('/pair/poll', { method: 'POST', json: { pairingId, pollToken } }),
  pairApprove: (code: string) => api<{ ok: true }>('/pair/approve', { method: 'POST', json: { code } }),
  rss: (url: string) => api<{ title: string; items: FeedItem[] }>(`/feeds/rss?url=${encodeURIComponent(url)}`),
  ics: (url: string, days: number) =>
    api<{ events: CalendarEvent[] }>(
      `/feeds/ics?url=${encodeURIComponent(url)}&days=${days}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`,
    ),
};
